/**
 * Escalation tiers against the dissemination channels: for every tier, how many
 * of this run's dispatches go out on each channel. Every channel the catalogue
 * defines is a column, with its latency; a zero is this run's own count.
 */

import type { Channel, DispatchEvent } from './shared'

interface Props {
  channels: Record<string, Channel>
  tiers: Record<string, string>
  events: DispatchEvent[]
}

function latency(minutes: number): string {
  return minutes < 1 ? `${Math.round(minutes * 60)} s` : `${minutes} min`
}

export function ChannelMatrix({ channels, tiers, events }: Props) {
  const columns = Object.entries(channels).sort((a, b) => a[1].latency_min - b[1].latency_min)
  const tierKeys = Object.keys(tiers).sort((a, b) => Number(a) - Number(b))
  if (!columns.length || !tierKeys.length) return null
  const count = (tier: number, channel: string) =>
    events.filter((e) => e.tier === tier && e.channel_key === channel).length

  return (
    <section className="panel" style={{ padding: 0 }} data-testid="channels">
      <div className="overflow-x-auto px-4 pb-3 pt-2">
        <table className="data-table w-full" style={{ minWidth: 960 }}>
          <thead>
            <tr>
              <th>tier</th>
              {columns.map(([key, c]) => (
                <th key={key} className="num" title={c.reach}>
                  <span style={{ display: 'block', whiteSpace: 'normal' }}>{c.label}</span>
                </th>
              ))}
            </tr>
            <tr>
              <td style={{ color: 'var(--ink-text-faint)' }}>latency</td>
              {columns.map(([key, c]) => (
                <td key={key} className="num" style={{ color: 'var(--ink-text-faint)' }}>
                  {latency(c.latency_min)}
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {tierKeys.map((tier) => (
              <tr key={tier}>
                <td>
                  <span style={{ color: 'var(--ink-text)' }}>Tier {tier}</span>
                  <span style={{ color: 'var(--ink-text-dim)' }}> · {tiers[tier]}</span>
                </td>
                {columns.map(([key]) => {
                  const n = count(Number(tier), key)
                  return (
                    <td key={key} className="num" style={{ color: n ? 'var(--ink-text)' : 'var(--ink-text-faint)' }}>
                      {n}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
